import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router';

/**
 * A route whose `meta.overlayParam` names an optional param drives something over a page that
 * stays put: a fly-in panel (`/plm/products/:articleId?`), a file preview, the selected chart in
 * an editor. Two things follow from the page staying put, and both read this one flag:
 *
 * - The navigation loader is skipped (`entry-client.ts`) — it would cover the very thing the
 *   user is waiting to see, and these panels bring their own skeleton.
 * - The navigation replaces rather than pushes (`installOverlayHistory`) — someone who opened
 *   five articles and then reaches for the back button wants the page they came from, not the
 *   panel flying in and out five times.
 */
export function installOverlayHistory(router: Router) {
  const push = router.push;
  const replace = router.replace;

  router.push = (to: RouteLocationRaw) =>
    isOverlayNavigation(router, to, router.currentRoute.value) ? replace(to) : push(to);
}

/** True when `to` differs from `from` by nothing but `from`'s overlay param. */
export function isOverlayNavigation(router: Router, to: RouteLocationRaw, from: RouteLocationNormalized): boolean {
  const overlayParam = from.meta.overlayParam;
  if (typeof overlayParam !== 'string') return false;

  const target = router.resolve(to);
  if (target.name !== from.name || target.meta.overlayParam !== overlayParam) return false;

  // Put the overlay back where it is now: if that lands exactly where we already are, the overlay
  // was the only thing the navigation changed. Comparing the resolved paths this way covers the
  // other params, the query and the hash without spelling any of them out.
  const withoutOverlay = router.resolve({
    name: target.name,
    params: { ...target.params, [overlayParam]: from.params[overlayParam] },
    query: target.query,
    hash: target.hash,
  });

  return withoutOverlay.fullPath === from.fullPath;
}
